import apiClient from './apiClient'

export interface EmployeeListItem {
  id: number
  fullName: string
  email: string
  phone: string | null
  employmentType: number
  workingRatioPercent: number
  hireDate: string
  status: number
  departmentName: string | null
  positionName: string | null
  hasEntraId: boolean
  managerFullName: string | null
  workScheduleJson: string | null
  weeklyHours: number             // #23: týdenní úvazek v hodinách
  vacationReviewNeeded: boolean   // #20: nárok daného roku je potřeba přepočítat (změna úvazku)
}

// Typy pracovního poměru
export const EMPLOYMENT_TYPE: Record<number, { label: string; isFullTime: boolean }> = {
  1: { label: 'HPP',  isFullTime: true  },
  2: { label: 'DPP',  isFullTime: false },
  3: { label: 'DPČ',  isFullTime: false },
}

// Stavy zaměstnance
export const EMPLOYEE_STATUS: Record<number, { label: string; color: 'success' | 'warning' | 'subtle' | 'danger' }> = {
  1: { label: 'Aktivní',             color: 'success' },
  2: { label: 'Nenastoupil',         color: 'subtle'  },
  3: { label: 'Ukončen',             color: 'subtle'  },
  4: { label: 'Rodičovská dovolená', color: 'warning' },
}

export interface EmployeeDetail {
  id: number
  firstName: string
  lastName: string
  fullName: string
  // Pracovní kontakt
  email: string
  phone: string | null
  costCenter: string | null
  // Soukromý kontakt
  personalEmail: string | null
  personalPhone: string | null
  // Osoba blízká
  nextOfKinName: string | null
  nextOfKinAddress: string | null
  nextOfKinEmail: string | null
  nextOfKinPhone: string | null
  // Adresa
  address: string | null
  city: string | null
  postalCode: string | null
  country: string | null
  // Osobní
  birthDate: string | null
  personalId: string | null
  healthInsurance: string | null
  healthInsuranceCode: string | null
  // Pracovní údaje
  hireDate: string
  cooperationStartDate: string | null
  terminationDate: string | null
  statusFrom: string | null
  employmentType: number
  status: number
  // Smlouva
  contractType: number
  contractEndDate: string | null
  probationEndDate: string | null
  departmentId: number | null
  departmentName: string | null
  positionId: number | null
  positionName: string | null
  managerId: number | null
  managerFullName: string | null
  entraObjectId: string | null
  personalNumber: string | null
  hasMealVouchers: boolean
  workingRatioPercent: number
  weeklyHours: number
  workScheduleJson: string | null
  // Bankovní
  bankAccount: string | null
  // Vozidlo
  vehicle: string | null
  // Děti
  child1Name: string | null
  child1PersonalId: string | null
  child1Studies: boolean
  child2Name: string | null
  child2PersonalId: string | null
  child2Studies: boolean
  child3Name: string | null
  child3PersonalId: string | null
  child3Studies: boolean
  child4Name: string | null
  child4PersonalId: string | null
  child4Studies: boolean
  child5Name: string | null
  child5PersonalId: string | null
  child5Studies: boolean
  // Poznámka
  note: string | null
}

export interface WorkSchedule {
  mon: number
  tue: number
  wed: number
  thu: number
  fri: number
}

export interface GetEmployeesResult {
  items: EmployeeListItem[]
  totalCount: number
  page: number
  pageSize: number
}

export interface CreateEmployeeRequest {
  firstName: string
  lastName: string
  // Pracovní kontakt
  email: string
  phone?: string
  costCenter?: string
  // Soukromý kontakt
  personalEmail?: string
  personalPhone?: string
  // Osoba blízká
  nextOfKinName?: string
  nextOfKinAddress?: string
  nextOfKinEmail?: string
  nextOfKinPhone?: string
  // Adresa
  address?: string
  city?: string
  postalCode?: string
  country?: string
  // Osobní
  birthDate?: string
  personalId?: string
  healthInsurance?: string
  healthInsuranceCode?: string
  // Pracovní údaje
  hireDate: string
  cooperationStartDate?: string
  employmentType: number
  status?: number
  statusFrom?: string
  // Smlouva
  contractType?: number
  contractEndDate?: string
  probationEndDate?: string
  departmentName?: string
  positionName?: string
  managerId?: number
  entraObjectId?: string
  personalNumber?: string
  hasMealVouchers?: boolean
  workingRatioPercent?: number
  weeklyHours?: number            // #23 Fáze 1: týdenní úvazek v hodinách (primární)
  workloadValidFrom?: string      // #23 Fáze 2: datum platnosti úvazku (nové období / oprava data aktuálního)
  workloadIsNew?: boolean         // #23: true = nové období od data; false = oprava stávajícího (aktuálního)
  workScheduleJson?: string
  // Bankovní
  bankAccount?: string
  // Vozidlo
  vehicle?: string
  // Děti
  child1Name?: string
  child1PersonalId?: string
  child1Studies?: boolean
  child2Name?: string
  child2PersonalId?: string
  child2Studies?: boolean
  child3Name?: string
  child3PersonalId?: string
  child3Studies?: boolean
  child4Name?: string
  child4PersonalId?: string
  child4Studies?: boolean
  child5Name?: string
  child5PersonalId?: string
  child5Studies?: boolean
  // Poznámka
  note?: string
}

export const employeesService = {
  getAll: (params?: { search?: string; departmentId?: number; status?: number; excludeTerminated?: boolean; page?: number; pageSize?: number; managerSearch?: string }) =>
    apiClient.get<GetEmployeesResult>('/employees', { params }).then(r => r.data),

  getById: (id: number) =>
    apiClient.get<EmployeeDetail>(`/employees/${id}`).then(r => r.data),

  create: (data: CreateEmployeeRequest) =>
    apiClient.post<{ id: number }>('/employees', data).then(r => r.data),

  update: (id: number, data: Partial<EmployeeDetail>) =>
    apiClient.put(`/employees/${id}`, { id, ...data }),

  delete: (id: number) =>
    apiClient.delete(`/employees/${id}`),

  checkEmail: (email: string) =>
    apiClient.get<{ isTaken: boolean; employeeName: string | null }>('/employees/check-email', { params: { email } }).then(r => r.data),

  getWorkloadHistory: (id: number) =>
    apiClient.get<WorkloadPeriod[]>(`/employees/${id}/workload-history`).then(r => r.data),

  addWorkloadPeriod: (id: number, data: { validFrom: string; weeklyHours: number }) =>
    apiClient.post(`/employees/${id}/workload-history`, data),

  updateWorkloadPeriod: (id: number, periodId: number, data: { validFrom: string; weeklyHours: number }) =>
    apiClient.put(`/employees/${id}/workload-history/${periodId}`, data),

  deleteWorkloadPeriod: (id: number, periodId: number) =>
    apiClient.delete(`/employees/${id}/workload-history/${periodId}`),
}

// #23 Fáze 2: historie úvazku
export interface WorkloadPeriod {
  id: number
  validFrom: string
  weeklyHours: number
  workScheduleJson: string | null
}
